import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import {
  useDeleteStreams,
  useGetGroup,
  usePutGroup,
  useTransferGroupStreams,
} from '@vlabs/api/artemy/client';
import type {
  PutGroupRequest,
  StreamId,
  StreamAnalytics,
} from '@vlabs/api/artemy/model';
import {
  buildDefaultStreamAnalytics,
  getAnalyticsTypeLabel,
  nextAnalyticsLabel,
  parseAnalyticsLabel,
  SourceGeoposition,
  stripEmpty,
  toFormStreamSettings,
  toRequestStreamSettings,
} from '@vlabs/monokit/entities/sources';
import { FacestreamAnalytic } from '@vlabs/monokit/entities/videoanalytics';
import { UnsavedChangesModal } from '@vlabs/monokit/features/unsaved-changes-modal';
import { getApiErrorInfo, parseValidationDetail, setServerFieldErrors } from '@vlabs/monokit/shared/lib/errors';
import { useGroupIdFromLocation } from '@vlabs/monokit/shared/lib/use-group-id-from-location';
import { useUnsavedChangesToast } from '@vlabs/monokit/shared/lib/use-unsaved-changes-toast';
import { Page, PageContent } from '@vlabs/monokit/shared/ui/page';
import { SideNavLayout } from '@vlabs/monokit/shared/ui/side-nav-layout';
import { SourceGroupGeneralInfo } from '@vlabs/monokit/widgets/source-group-general-info';
import type { GroupSettingsFormValues } from '@vlabs/monokit/widgets/source-group-general-info';
import { SourceGroupInfo } from '@vlabs/monokit/widgets/source-group-info';
import {
  mapErrorsToSections,
  SourceGroupSettingsNav,
} from '@vlabs/monokit/widgets/source-group-settings-nav';
import type {
  SourceGroupAnalyticsItem,
  SourceGroupSection,
} from '@vlabs/monokit/widgets/source-group-settings-nav';
import { SourceGroupStreamSettings } from '@vlabs/monokit/widgets/source-group-stream-settings';
import { SourceGroupStreams } from '@vlabs/monokit/widgets/source-group-streams';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useSourceGroupPageTranslation } from './i18n';
import st from './SourceGroupPage.module.sass';
import { SourceGroupPageHeader } from './SourceGroupPageHeader';
import { routes } from '../../app/routes';

interface PendingStreamOps {
  transfers: { streamIds: StreamId[]; targetGroupId: string }[];
  deletes: StreamId[];
}

const UNSAVED_TOAST_ID = 'source-group-unsaved';

function SourceGroupPage(): React.JSX.Element {
  const { t } = useSourceGroupPageTranslation();
  // Кросс-ns ключи типов ВА не резолвятся через префиксный t
  const groupId = useGroupIdFromLocation();

  const getGroup = useGetGroup(groupId);
  const group = getGroup.data;

  const methods = useForm<GroupSettingsFormValues>({
    defaultValues: {
      group_name: '',
      description: '',
      owner_department: '',
      permission_tag: '',
      stream_settings: toFormStreamSettings(undefined),
      analytics: [],
    },
  });
  const {
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = methods;

  const analytics = watch('analytics');

  const [activeSection, setActiveSection] = useState<SourceGroupSection>('general');
  const [sectionErrors, setSectionErrors] = useState<Partial<Record<SourceGroupSection, boolean>>>({});

  // Отложенные операции над источниками: применяются только по «Сохранить» (GUI-3948)
  const [pendingStreamOps, setPendingStreamOps] = useState<PendingStreamOps>({
    transfers: [],
    deletes: [],
  });

  const putGroup = usePutGroup();
  const transferGroupStreams = useTransferGroupStreams();
  const deleteStreams = useDeleteStreams();

  const hasPendingOps = pendingStreamOps.transfers.length > 0 || pendingStreamOps.deletes.length > 0;
  const hasUnsavedChanges = isDirty || hasPendingOps;

  const hiddenStreamIds = useMemo(() => {
    const ids = new Set<string>(pendingStreamOps.deletes);
    pendingStreamOps.transfers.forEach(({ streamIds }) => {
      streamIds.forEach((id) => ids.add(id));
    });
    return ids;
  }, [pendingStreamOps]);

  useEffect(() => {
    if (!group) return;

    reset({
      group_name: group.group_name,
      description: group.description ?? '',
      owner_department: group.owner_department ?? '',
      permission_tag: group.permission_tag ?? '',
      stream_settings: toFormStreamSettings(group.stream_settings),
      analytics: group.analytics ?? [],
    });
  }, [group, reset]);

  // Если активная секция ВА исчезла (удаление), возвращаемся на «Общую информацию»
  useEffect(() => {
    if (!activeSection.startsWith('va:')) return;

    const index = Number(activeSection.slice(3));
    if (!analytics?.[index]) setActiveSection('general');
  }, [activeSection, analytics]);

  useUnsavedChangesToast(hasUnsavedChanges, t('unsaved_toast'), UNSAVED_TOAST_ID);

  const enqueueDelete = useCallback((streamIds: StreamId[]) => {
    setPendingStreamOps((prev) => ({
      ...prev,
      deletes: [...prev.deletes, ...streamIds.filter((id) => !prev.deletes.includes(id))],
    }));
  }, []);

  const enqueueTransfer = useCallback((streamIds: StreamId[], targetGroupId: string) => {
    setPendingStreamOps((prev) => ({
      ...prev,
      transfers: [...prev.transfers, { streamIds, targetGroupId }],
    }));
  }, []);

  // Добавление ВА: страница вне FormProvider, поэтому обновляем массив через setValue
  const addAnalytics = useCallback((type: string) => {
    const current = getValues('analytics') ?? [];
    const item = buildDefaultStreamAnalytics(type, nextAnalyticsLabel(current, type));

    setValue('analytics', [...current, item], { shouldDirty: true });
    setActiveSection(`va:${current.length}`);
  }, [getValues, setValue]);

  const analyticsNavItems: SourceGroupAnalyticsItem[] = (analytics ?? []).map((item, index) => {
    const labelParts = parseAnalyticsLabel(item.label);
    const title = getAnalyticsTypeLabel(item.type)
      + (labelParts ? ` ${labelParts.number}` : '');

    return { key: `va:${index}`, title };
  });

  const saveForm = handleSubmit(async (values) => {
    try {
      const data: PutGroupRequest = {
        group_name: values.group_name,
        owner_department: values.owner_department,
        permission_tag: values.permission_tag,
        stream_settings: toRequestStreamSettings(values.stream_settings),
        analytics: stripEmpty(values.analytics) as StreamAnalytics[],
      };
      // ZEN #9: пустую строку описания в api не шлём
      if (values.description) {
        data.description = values.description;
      }

      await putGroup.mutateAsync({
        groupId,
        data,
        params: { streams_update: 1 },
      });

      toast.success(t('save_success'));
      setSectionErrors({});
      reset(getValues());
    } catch (error) {
      const apiError = getApiErrorInfo(error);
      const fieldErrors = parseValidationDetail(apiError.detail);

      if (fieldErrors.length === 0) {
        toast.error(apiError.desc ?? t('save_error'));
        return;
      }

      setServerFieldErrors(setError, fieldErrors);
      setSectionErrors(mapErrorsToSections(fieldErrors));
      toast.error(t('save_validation_error'));
    }
  });

  // Выполненные шаги убираются из очереди: при ошибке остаток сохраняется,
  // повторный клик «Сохранить» не дублирует выполненное
  const runPendingOps = async (): Promise<boolean> => {
    const { transfers, deletes } = pendingStreamOps;

    let allOk = true;
    await transfers.reduce(async (chain, transfer) => chain.then(async () => {
      if (!allOk) return;

      try {
        await transferGroupStreams.mutateAsync({
          targetGroupId: transfer.targetGroupId,
          data: { stream_ids: transfer.streamIds },
        });
        setPendingStreamOps((prev) => ({
          ...prev,
          transfers: prev.transfers.filter((item) => item !== transfer),
        }));
      } catch {
        allOk = false;
        toast.error(t('save_error'));
      }
    }), Promise.resolve());

    if (!allOk || deletes.length === 0) return allOk;

    try {
      await deleteStreams.mutateAsync({ data: { stream_ids: deletes } });
      setPendingStreamOps((prev) => ({ ...prev, deletes: [] }));
      return true;
    } catch {
      toast.error(t('save_error'));
      return false;
    }
  };

  const onSave = async () => {
    if (hasPendingOps) {
      const opsSaved = await runPendingOps();
      if (!opsSaved) return;
    }

    if (isDirty) {
      await saveForm();
      return;
    }

    toast.success(t('save_success'));
  };

  const sectionContent = useMemo(() => {
    switch (activeSection) {
      case 'general':
        return (
          <div className={st.Section}>
            <SourceGroupGeneralInfo groupId={groupId} />
          </div>
        );
      case 'streams':
        return (
          <SourceGroupStreams
            group={group}
            hiddenStreamIds={hiddenStreamIds}
            onEnqueueDelete={enqueueDelete}
            onEnqueueTransfer={enqueueTransfer}
          />
        );
      case 'stream-settings':
        return (
          <div className={st.Section}>
            <SourceGroupStreamSettings />
          </div>
        );
      default:
        if (activeSection.startsWith('va:')) {
          const index = Number(activeSection.slice(3));
          if (analytics?.[index]) {
            return (
              <div className={st.Section}>
                <FacestreamAnalytic index={index} />
              </div>
            );
          }
        }
        return null;
    }
  }, [activeSection, analytics, enqueueDelete, enqueueTransfer, group, groupId, hiddenStreamIds]);

  if (getGroup.isLoading) {
    return (
      <Page>
        <PageContent>
          <Loader />
        </PageContent>
      </Page>
    );
  }

  if (getGroup.isError || !group) {
    return (
      <Page>
        <PageContent>
          <div className={st.ErrorBlock}>
            <span>{t('load_error')}</span>
            <Link to={routes.sources.list}>{t('back_to_list')}</Link>
          </div>
        </PageContent>
      </Page>
    );
  }

  let info: React.JSX.Element | undefined;
  if (activeSection !== 'streams') {
    info = (
      <React.Fragment>
        {activeSection === 'stream-settings' && (
          <div className={st.InfoCard}>
            <SourceGeoposition
              defaultValues={watch('stream_settings.location')}
              onSubmit={(next) => {
                setValue('stream_settings.location', next, { shouldDirty: true });
              }}
            />
          </div>
        )}
        <SourceGroupInfo />
      </React.Fragment>
    );
  }

  return (
    <Page>
      <PageContent>
        <FormProvider {...methods}>
          <SourceGroupPageHeader
            groupId={groupId}
            groupName={group.group_name}
            isDirty={hasUnsavedChanges}
            isSaving={putGroup.isPending || transferGroupStreams.isPending || deleteStreams.isPending}
            onSave={onSave}
          />

          <SideNavLayout
            info={info}
            main={sectionContent}
            nav={(
              <SourceGroupSettingsNav
                activeSection={activeSection}
                analyticsItems={analyticsNavItems}
                onAddAnalytics={addAnalytics}
                onChange={setActiveSection}
                sectionErrors={sectionErrors}
              />
            )}
          />

          <UnsavedChangesModal when={hasUnsavedChanges} />
        </FormProvider>
      </PageContent>
    </Page>
  );
}

export { SourceGroupPage };
